import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/footer';
import PhotoCarousel from '../components/carousel';
import Quotes from '../components/quotes';
import Class from '../components/class';
import Question from '../components/question';
import '../styles/home.modules.css';
import Counter from '../components/counter';
import Seo from '../components/Seo';
import { useSchedule, usePages } from '../data/useLocations';
import {
    SkeletonStyles,
    StatsSkeleton,
    DisciplinesSkeleton,
    TestimonialsSkeleton,
    NewsSkeleton,
    LocationsSkeleton,
    FaqSkeleton,
} from '../components/homeSkeleton';

const SR_ONLY = {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
    border: 0,
};

/** Liczba kolumn w sekcji "Nasze placówki". */
const LOCATION_COLUMNS = 3;

function Home() {
    const navigate = useNavigate();

    const { data, loading } = useSchedule();
    const { pages } = usePages();

    const stats = data?.stats || [];
    const disciplines = data?.disciplines || [];
    const testimonials = data?.testimonials || [];
    const news = data?.news || [];
    const faq = data?.faq || [];

    /** Dane strukturalne FAQ — niewidoczne, obsługiwane przez Google. */
    const faqSchema = useMemo(() => {
        if (faq.length === 0) return null;

        return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((x) => ({
                "@type": "Question",
                name: x.question,
                acceptedAnswer: { "@type": "Answer", text: x.answer },
            })),
        };
    }, [faq]);

    const locationColumns = useMemo(() => {
        if (pages.length === 0) return [];

        const perColumn = Math.ceil(pages.length / LOCATION_COLUMNS);
        const cols = [];

        for (let i = 0; i < LOCATION_COLUMNS; i++) {
            const slice = pages.slice(i * perColumn, (i + 1) * perColumn);
            if (slice.length > 0) cols.push(slice);
        }

        return cols;
    }, [pages]);

  return (
    <main style={({position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", gap: "35px", width: "100%"})}>
        <Seo
        title="Szkoła tańca i akrobatyki powietrznej dla dzieci"
        description="Zajęcia z akrobatyki powietrznej, tańca i gimnastyki dla dzieci od 3 lat. 22 placówki we Wrocławiu, Jastrzębiu-Zdroju i okolicach. Sprawdź grafik na sezon 2026/2027."
        path="/"
        />
        <h1 style={SR_ONLY}>
            4STARS Dance &amp; Aerial Studio - szkoła tańca i akrobatyki powietrznej dla dzieci we Wrocławiu, Jastrzębiu-Zdroju i okolicach
        </h1>

        {faqSchema ? (
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
        ) : null}

        <div className='content' style={({position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", gap: "65px", width: "100%"})}>
            <SkeletonStyles />
            <PhotoCarousel />

            {stats.length > 0 ? (
                <section className="recordSection" style={({display: "flex", width: "92%", marginTop: "-10px", maxWidth: "1000px"})}>
                    {stats.map((s) => (
                        <div key={s.id} className="recordSection_panel" style={({display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#d0142d", borderRadius: "8px"})}>
                            {s.icon ? <img src={`/${s.icon}`} alt="" /> : null}
                            {s.prefix ? (
                                <span className="recordSection_span" style={({fontWeight: "normal", color: "rgb(250,250,250)"})}>{s.prefix}</span>
                            ) : null}
                            <Counter end={s.value} duration={2.5}/>
                            <span className="recordSection_span" style={({fontWeight: "normal", color: "rgb(250,250,250)"})}>{s.label}</span>
                        </div>
                    ))}
                </section>
            ) : null}

            <hr style={({border: "2px solid #d0142d", width: '80%', borderRadius: "10px"})} />
            <h2 style={({color: "rgb(30,30,30)", fontSize: "23px", fontWeight: "bold", marginBottom: "-5px", marginTop: "-25px", textAlign: "center", width: "auto"})}>Sprawdź naszą ofertę!</h2>
            <section className="offertSectionMain" style={({display: "flex", flexDirection: "column", alignItems: "center"})}>
                <div className="offertSection" style={({display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"})}>
                    <Link className="offertSection_panel_1" to={"/lokalizacje"} style={({textDecoration: "none"})}>
                        <div style={({display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", backgroundColor: "#d0142d", borderRadius: "8px", padding: "0% 4%", backgroundImage: "url(locations.png)", backgroundRepeat: "no-repeat", backgroundPosition: "right", cursor: "pointer", width: "100%", height: "100%"})}>
                            <h3 style={({color: "rgb(250,250,250)", fontSize: "16px", fontWeight: "bold", marginBottom: "5px", marginTop: "30px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)", transition: "transform 0.3s ease, text-shadow 0.3s ease"})}>Znajdź lokalizację w Twoim mieście!</h3>
                            <p style={({color: "rgb(250,250,250)", fontSize: "13px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)", transition: "transform 0.3s ease, text-shadow 0.3s ease"})}>Oferty placówek różnią się miedzy sobą.</p>
                        </div>
                    </Link>
                    <div className="offertSection_panel_1" style={({textDecoration: "none"})} onClick={() => document.getElementById("style")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                        <div style={({display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", backgroundColor: "#d0142d", borderRadius: "8px", padding: "0% 4%", backgroundImage: "url(classes.png)", backgroundRepeat: "no-repeat", backgroundPosition: "right", cursor: "pointer", width: "100%", height: "100%"})}>
                            <h3 style={({color: "rgb(250,250,250)", fontSize: "16px", fontWeight: "bold", marginBottom: "5px", marginTop: "30px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)", transition: "transform 0.3s ease, text-shadow 0.3s ease"})}>Zajęcia dla dzieci i dorosłych!</h3>
                            <p style={({color: "rgb(250,250,250)", fontSize: "13px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)", transition: "transform 0.3s ease, text-shadow 0.3s ease"})}>W 4STARS każdy znajdzie coś dla siebie!</p>
                        </div>
                    </div>
                </div>
                <div className="offertSection" style={({display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"})}>
                    <Link className="offertSection_panel_2" to={"/urodziny"} style={({textDecoration: "none"})}>
                        <div style={({display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", backgroundColor: "#d0142d", borderRadius: "8px", padding: "0% 5%", backgroundImage: "url(birthdays.png)", backgroundRepeat: "no-repeat", backgroundPosition: "right", cursor: "pointer", width: "100%", height: "100%"})}>
                            <h3 style={({color: "rgb(250,250,250)", fontSize: "16px", fontWeight: "bold", marginBottom: "5px", marginTop: "30px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)"})}>Urodziny</h3>
                            <p style={({color: "rgb(250,250,250)", fontSize: "13px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)"})}>Oferta urodzin dla dzieci!</p>
                        </div>
                    </Link>
                    <Link
                        to="/wydarzenia"
                        className="offertSection_panel_2"
                        style={({textDecoration: "none"})}
                    >
                        <div style={({display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", backgroundColor: "#d0142d", borderRadius: "8px", padding: "0% 5%", backgroundImage: "url(events.png)", backgroundRepeat: "no-repeat", backgroundPosition: "right", cursor: "pointer", width: "100%", height: "100%"})}>
                            <h3 style={({color: "rgb(250,250,250)", fontSize: "16px", fontWeight: "bold", marginBottom: "5px", marginTop: "30px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)"})}>Wydarzenia specjalne</h3>
                            <p style={({color: "rgb(250,250,250)", fontSize: "13px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)"})}>Warsztaty, nocowanki i wiele więcej!</p>
                        </div>
                    </Link>
                    <Link className="offertSection_panel_2" to={"/zajecia-indywidualne"} style={({textDecoration: "none"})}>
                        <div style={({display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", backgroundColor: "#d0142d", borderRadius: "8px", padding: "0% 5%", backgroundImage: "url(individual.png)", backgroundRepeat: "no-repeat", backgroundPosition: "right", cursor: "pointer", width: "100%", height: "100%"})}>
                            <h3 style={({color: "rgb(250,250,250)", fontSize: "16px", fontWeight: "bold", marginBottom: "5px", marginTop: "30px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)"})}>Zajęcia indywidualne</h3>
                            <p style={({color: "rgb(250,250,250)", fontSize: "13px", textShadow: "0 2px 2px rgba(0,0,0,0.2), 0 4px 4px rgba(0,0,0,0.15), 0 8px 8px rgba(0,0,0,0.1)"})}>Oferta zajęć indywidualnych!</p>
                        </div>
                    </Link>
                </div>
            </section>

            {disciplines.length > 0 ? (
                <section id="style" style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", width: "100%", gap: "10px", backgroundColor: "#d0142d", paddingBottom: "50px"})}>
                    <h2 style={({color: "rgb(250,250,250)", fontSize: "23px", fontWeight: "bold", marginTop: "35px", textAlign: "center", width: "auto"})}>Poznaj zajęcia dla siebie!</h2>
                    <div style={({display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "center", width: "90%", marginTop: "20px", flexWrap: "wrap", gap: "30px"})}>
                        {disciplines.map((d) => (
                            <Class
                                key={d.id}
                                image={d.iconUrl || (d.icon ? `/${d.icon}` : "")}
                                text={d.name}
                            />
                        ))}
                    </div>
                </section>
            ) : null}

            <section className="stypendiumSection" style={({display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start", backgroundColor: "#d0142d", gap: "5px"})}>
                <h3 style={({color: "rgb(250,250,250)", fontWeight: "bold", marginBottom: "5px"})}>STYPENDIUM 4STARS</h3>
                <span style={({color: "rgb(250,250,250)", fontWeight: "normal"})}>Tylko w 4STARS masz szanse na zdobycie stypendium, które pomoże ci w finansowaniu dalszej nauki!</span>
                <button style={({color: "#d0142d" ,backgroundColor: "rgb(250,250,250)", borderRadius: "13px", fontSize: "13px", padding: "5px 10px", outline: "none", border: "none", cursor: "pointer", marginTop: "20px"})} onClick={() => navigate("/wkrotce-dostepne")}>Dowiedz się więcej</button>
            </section>

            <hr style={({border: "2px solid #d0142d", width: '80%', borderRadius: "10px"})} />
            <h2 style={({color: "rgb(30,30,30)", fontSize: "23px", fontWeight: "bold", marginBottom: "-5px", marginTop: "-25px", textAlign: "center", width: "auto"})}>Co nas wyróżnia?</h2>
            <section className="atutSection" style={({display: "flex"})}>
                <div className="atutSection_panel" style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "15px"})}>
                    <div style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px", backgroundColor: "#d0142d"})}>
                        <img src="personel.png" alt="" style={({width: "45px", height: "45px"})}/>
                    </div>
                    <p style={({fontWeight: "bold", fontSize: "14px", color: "rgb(30,30,30)", textAlign: "center"})}>Wyszkolona i doświadczona kadra!</p>
                    <p className="atutSection_text" style={({fontWeight: "normal", fontSize: "12px", color: "rgb(30,30,30)", textAlign: "center"})}>Każdy Trener jest przez nas dokładnie szkolony <br/> w swojej dziedzinie!</p>
                </div>
                <div className="atutSection_panel" style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "15px"})}>
                    <div style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px", backgroundColor: "#d0142d"})}>
                        <img src="indiv.png" alt="" style={({width: "45px", height: "45px"})}/>
                    </div>
                    <p style={({fontWeight: "bold", fontSize: "14px", color: "rgb(30,30,30)", textAlign: "center"})}>Indywidualne podejście!</p>
                    <p className="atutSection_text" style={({fontWeight: "normal", fontSize: "12px", color: "rgb(30,30,30)", textAlign: "center"})}>Każdy uczestnik ma swoją przestrzeń, własną szarfę, czy koło. Instruktor skupia się na poziomie każdego uczestnika!</p>
                </div>
                <div className="atutSection_panel" style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "15px"})}>
                    <div style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px", backgroundColor: "#d0142d"})}>
                        <img src="idea.png" alt="" style={({width: "45px", height: "45px"})}/>
                    </div>
                    <p style={({fontWeight: "bold", fontSize: "14px", color: "rgb(30,30,30)", textAlign: "center"})}>Własny program naucznia!</p>
                    <p className="atutSection_text" style={({fontWeight: "normal", fontSize: "12px", color: "rgb(30,30,30)", textAlign: "center"})}>Zajęcia prowadzone są według naszego autorskiego planu szkolenia uczestników!</p>
                </div>
            </section>

            {testimonials.length > 0 ? (
                <>
                    <h2 style={({color: "rgb(30,30,30)", fontSize: "23px", fontWeight: "bold", marginBottom: "-15px", marginTop: "10px", textAlign: "center", width: "auto"})}>Opinie naszych klientów!</h2>
                    <section className="qouteSection" style={({display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "30px", overflowX: "auto", flexWrap: "no-wrap", paddingBottom: "40px", maxWidth: "1200px"})}>
                        {testimonials.map((t) => (
                            <Quotes key={t.id} text={t.text} desc={t.author} />
                        ))}
                    </section>
                </>
            ) : null}

            {news.length > 0 ? (
                <>
                    <hr style={({border: "2px solid #d0142d", width: '80%', borderRadius: "10px", marginTop: "-15px"})} />
                    <h2 style={({color: "rgb(30,30,30)", fontSize: "23px", fontWeight: "bold", marginBottom: "-5px", marginTop: "-15px", textAlign: "center", width: "auto"})}>Nasze promocje i aktualności!</h2>
                    <section className="newsSection" style={({display: "flex", alignItems: "center", maxWidth: "1200px"})}>
                        {news.map((n) => (
                            <div key={n.id} className="newsSection_panel" style={({display: "flex", justifyContent: "flex-start", border: "1px solid #eee", boxShadow: "0 5px 10px rgba(0,0,0,0.1)", position: 'relative', backgroundColor: "rgb(250,250,250)"})}>
                                <img src={n.imageUrl} alt={n.title} loading="lazy" />
                                <div style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", width: "100%"})}>
                                    <h3 style={({fontWeight: "bold", color: "rgb(30, 30, 30)", marginTop: "10px", textAlign: "left", width: "90%"})}>{n.title}</h3>
                                    <h4 style={({color: "rgb(30, 30, 30)", fontWeight: "400", textAlign: "left", width: "90%", marginTop: "-3px"})}>{n.text}</h4>
                                </div>
                                {n.ctaPath ? (
                                    <Link
                                        to={n.ctaPath}
                                        className="newsSection_button"
                                        style={{ backgroundColor: "#d0142d", padding: "5px 16px", borderRadius: "20px", color: "rgb(250,250,250)", fontWeight: "bold", cursor: "pointer", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "10px", position: "absolute", bottom: "10px", right: "15px", textDecoration: "none" }}
                                    >
                                        {n.ctaLabel}
                                    </Link>
                                ) : n.ctaHref ? (
                                    <a
                                        href={n.ctaHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="newsSection_button"
                                        style={{ backgroundColor: "#d0142d", padding: "5px 16px", borderRadius: "20px", color: "rgb(250,250,250)", fontWeight: "bold", cursor: "pointer", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "10px", position: "absolute", bottom: "10px", right: "15px", textDecoration: "none" }}
                                    >
                                        {n.ctaLabel}
                                    </a>
                                ) : null}
                            </div>
                        ))}
                    </section>
                </>
            ) : null}

            {locationColumns.length > 0 ? (
                <>
                    <hr style={({border: "2px solid #d0142d", width: '80%', borderRadius: "10px"})} />
                    <h2 style={({color: "rgb(30,30,30)", fontSize: "23px", fontWeight: "bold", marginTop: "-25px", textAlign: "center", width: "auto", marginBottom: "-6px"})}>Nasze placówki</h2>
                    <section className="locationsSections" style={({display: "flex", alignItems: "flex-start", maxWidth: "1200px"})}>
                        {locationColumns.map((col, idx) => (
                            <div key={idx} style={({display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start", width: "auto"})}>
                                {col.map((p) => (
                                    <Link key={p.slug} to={`/${p.slug}`} style={({textDecoration: "none"})}>
                                        <p style={({fontWeight: "bold", fontSize: "13px", color: "rgb(30,30,30)", textAlign: "left"})}>
                                            {p.city && p.city !== p.name ? `${p.city} - ${p.name}` : p.name}
                                        </p>
                                    </Link>
                                ))}
                            </div>
                        ))}
                    </section>
                </>
            ) : null}

            {faq.length > 0 ? (
                <>
                    <hr style={({border: "2px solid #d0142d", width: '80%', borderRadius: "10px", marginTop: "-10px"})} />
                    <h2 style={({color: "rgb(30,30,30)", fontSize: "23px", fontWeight: "bold", marginTop: "-30px", textAlign: "center", width: "auto"})}>Najczęstsze pytania</h2>
                    <section className="questionSection" style={({display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", gap: "15px", maxWidth: "1200px"})}>
                        {faq.map((f, i) => (
                            <React.Fragment key={f.id}>
                                {i > 0 ? (
                                    <hr style={({border: "1px solid rgb(0,0,0,0.1)", width: '100%', borderRadius: "20px", marginTop: "-5px"})} />
                                ) : null}
                                <Question question={f.question} answer={f.answer}/>
                            </React.Fragment>
                        ))}
                    </section>
                </>
            ) : null}
        </div>
        <Footer />
    </main>
  )
}

export default Home;